
import {
  ArrowsRightLeftIcon,
  CalendarIcon,
  MinusIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";
import { format, getISODay, parseISO, subDays } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { defineComponent, PropType } from "vue";
import BasicModal from "@/components/UI/BasicModal.vue";
import CopySectionData from "@/components/report/partials/CopySectionData.vue";
import SelectCombobox from "@/components/report/partials/SelectCombobox.vue";
import DailyReportRepository from "@/repositories/DailyReportRepository";
import logger from "@/services/logger";
import {
  ConfigListItem,
  ReportComboValues,
  ReportProcessEntry,
  ReportSectionConfig,
  ReportSettings,
} from "@/types/DailyReport";

export default defineComponent({
  name: "ProcessSection",
  components: {
    PencilIcon,
    MinusIcon,
    BasicModal,
    CalendarIcon,
    ArrowsRightLeftIcon,
    XMarkIcon,
    PlusIcon,
    SelectCombobox,
    CopySectionData,
  },
  props: {
    processes: {
      type: Array as PropType<ReportProcessEntry[]>,
      required: true,
    },
    editMode: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    highlightSection: {
      type: String as PropType<string>,
      required: true,
    },
    customFields: {
      type: Object as PropType<Record<string, ReportComboValues[]>>,
      required: true,
    },
    siteDuration: {
      type: Object as PropType<{ start_time: string | null; end_time: string | null }>,
      required: true,
    },
    settings: {
      type: Object as PropType<ReportSettings>,
      required: true,
    },
    validationError: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    approved: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    oaiEntries: {
      type: Array as PropType<ReportProcessEntry[]>,
      required: true,
    },
    sectionConfig: {
      type: Object as PropType<ReportSectionConfig>,
      required: true,
    },
  },
  emits: [
    "activeEditMode",
    "addHighlight",
    "addProcessEntry",
    "removeProcessEntry",
    "updateFieldValue",
    "updateComboBox",
    "addCustomField",
    "deleteCustomField",
    "updateSectionData",
  ],
  data() {
    return {
      openMergeProcessesModal: false as boolean,
      mergeableProcessesToEdit: [] as ReportProcessEntry[],
      searchDate: "",
      copyDataOpen: false,
      checkboxImbalancedNumber: false,
      checkboxSelectAllProcesses: false,
      date: this.$route.params.date,
    };
  },

  computed: {
    yesterday() {
      const dateObj = parseISO(Array.isArray(this.date) ? this.date[0] : this.date);
      return getISODay(dateObj) === 1
        ? format(subDays(dateObj, 2), "yyyy-MM-dd")
        : format(subDays(dateObj, 1), "yyyy-MM-dd");
    },
    groupedEntries() {
      const groupedLists: ReportProcessEntry[][] = [];
      for (const entry of this.processes) {
        const processType = entry.process_type.value;
        let existingList = groupedLists.find((list) => list[0]?.process_type.value === processType);

        if (!existingList) {
          existingList = [];
          groupedLists.push(existingList);
        }
        existingList.push(entry);
      }
      return groupedLists;
    },
    configList() {
      const result = Object.keys(this.sectionConfig).map((key) => ({
        ...this.sectionConfig[key],
        key,
      }));
      result.sort((a, b) => a.order - b.order);
      return result;
    },
    mergeableProcesses() {
      const mergeableProcesses = [] as ReportProcessEntry[];

      this.oaiEntries.forEach((entry) => {
        const isPresent = this.processes.some((item) => this.isProcessDuplicate(item, entry));

        if (!isPresent) {
          return mergeableProcesses.push({
            id: uuidv4(),
            process_type: entry.process_type,
            location: entry.location,
            start_time: entry.start_time,
            end_time: entry.end_time,
            note: entry.note,
            merge: isPresent,
          } as ReportProcessEntry);
        }
      });

      return mergeableProcesses;
    },
    hasMergeableProcesses() {
      return this.mergeableProcesses.some((item) => !item.merge);
    },
  },
  watch: {
    mergeableProcessesToEdit: {
      handler(newValue) {
        const countToMerge = newValue.filter((item: ReportProcessEntry) => item.merge).length;

        if (countToMerge === newValue.length) {
          this.checkboxImbalancedNumber = false;
          this.checkboxSelectAllProcesses = true;
        } else if (countToMerge === 0) {
          this.checkboxImbalancedNumber = false;
          this.checkboxSelectAllProcesses = false;
        } else {
          this.checkboxImbalancedNumber = true;
          this.checkboxSelectAllProcesses = false;
        }
      },
      deep: true,
    },
    checkboxSelectAllProcesses() {
      if (!this.checkboxImbalancedNumber) {
        this.mergeableProcessesToEdit.forEach((item) => {
          item.merge = this.checkboxSelectAllProcesses;
        });
      }
    },
  },

  methods: {
    activeEditMode() {
      this.$emit("activeEditMode");
      this.$emit("addHighlight");
    },

    addNewProcessEntry(type: ReportComboValues = { mode: "custom", value: "" }) {
      this.$emit("activeEditMode");
      const newEntry: ReportProcessEntry = {
        id: uuidv4(),
        process_type: type,
        location: {
          mode: "custom",
          value: "",
        },
        note: "",
        start_time: "",
        end_time: "",
      } as ReportProcessEntry;

      if (
        this.siteDuration?.start_time &&
        this.siteDuration?.end_time &&
        this.settings?.site_duration.set_process_duration
      ) {
        newEntry["start_time"] = this.siteDuration.start_time;
        newEntry["end_time"] = this.siteDuration.end_time;
      }
      this.$emit("addProcessEntry", newEntry, "processes");
    },

    copySectionData(date: string) {
      const { customer_name, site_id } = this.$route.params;

      return DailyReportRepository.getSectionValueCopy(
        "processes",
        customer_name as string,
        site_id as string,
        date,
      )
        .then((response) => {
          if (response.data.length === 0) {
            this.showToast("warning", this.$t("err.saved_values"));
          } else {
            this.$emit("updateSectionData", response.data);
          }
        })
        .catch(() => {
          this.showToast("warning", this.$t("err.saved_values"));
        })
        .finally(() => this.$emit("activeEditMode"));
    },

    isFirstCol(columnName: string) {
      if (this.configList.length > 0) {
        return this.configList[0].key === columnName;
      }
    },

    nextFocus(
      event: KeyboardEvent,
      groupIdx: number,
      rowIdx: number,
      columName: string,
      groupLength: number,
    ) {
      if (!event.shiftKey && !event.altKey && !event.ctrlKey) {
        if (this.configList.length > 0) {
          const configListCurrentIndex = this.configList.findIndex((item: ConfigListItem) => {
            return item.order === this.sectionConfig[columName].order;
          });

          if (configListCurrentIndex + 1 < this.configList.length && rowIdx < groupLength) {
            let increment = 1;
            if (!this.settings.site_duration.time_preview_enabled && columName === "location") {
              increment = 3; // Skip start_time and end_time
            }
            this.focusInputField(
              "processes",
              groupIdx,
              rowIdx,
              this.configList[configListCurrentIndex + increment].key,
            );
          } else if (
            configListCurrentIndex >= this.configList.length - 1 &&
            rowIdx < groupLength - 1
          ) {
            this.focusInputField("processes", groupIdx, rowIdx + 1, this.configList[1].key);
          } else if (
            rowIdx >= groupLength - 1 &&
            configListCurrentIndex >= this.configList.length - 1
          ) {
            if (groupIdx >= this.groupedEntries.length - 1) {
              this.addNewProcessEntry();
            }
            rowIdx = 0;

            this.focusInputField("processes", groupIdx + 1, rowIdx, this.configList[0].key);
          }
        }
      }
    },

    focusInputField(sectionTitle = "processes", group: number, row: number, columName: string) {
      let element = document.getElementById(`input-${sectionTitle}-${group}-${row}-${columName}`);
      if (element) {
        try {
          if (element.nodeName === "DIV") {
            const inputFields = element.querySelectorAll("input");
            if (inputFields.length > 0) {
              element = inputFields[0];
            } else {
              return;
            }
          }
          element.focus();
        } catch (error) {
          logger.error(error);
        }
      }
    },

    isProcessDuplicate(process: ReportProcessEntry, oaiProcess: ReportProcessEntry) {
      return (
        process.start_time === oaiProcess.start_time &&
        process.end_time === oaiProcess.end_time &&
        process.process_type.value === oaiProcess.process_type.value &&
        process.location.value === oaiProcess.location.value
      );
    },

    openMergeModal() {
      this.mergeableProcessesToEdit = structuredClone(this.mergeableProcesses);
      this.openMergeProcessesModal = true;
    },

    mergeProcesses() {
      this.mergeableProcessesToEdit.forEach((entry) => {
        if (entry.merge) {
          this.$emit(
            "addProcessEntry",
            {
              id: entry.id,
              process_type: entry.process_type,
              location: entry.location,
              start_time: entry.start_time,
              end_time: entry.end_time,
              note: entry.note,
            } as ReportProcessEntry,
            "processes",
          );
        }
      });
      this.openMergeProcessesModal = false;
      this.$emit("activeEditMode");
    },
  },
});
